import React from "react";
import styled, { css, keyframes } from "styled-components";
import { useLocation } from "react-router-dom";

import { Nav } from "../components/Nav";
import Footer from "../components/Footer";
import { Icon } from "../components/Icon";
import FormularioDiagnostico from "../components/FormularioDiagnostico";
import { DIAGNOSTICO, FRENTES, FAQ_GERAL, TRUST, CLIENTES, waLink } from "../data/diagnostico";
import { PROJECT_CASES } from "../data/projects";
import {
    SITE_URL,
    setMetaTag,
    setLinkTag,
    setStructuredData,
    removeStructuredData,
    restoreDefaultMeta
} from "../utils/pageMeta";

import HeroCutout from "../assets/media/hero-fundo-vazado.png";

const HOME_TITLE = "Mango Software House · Software sob medida, automação e sistemas para PME";
const HOME_DESCRIPTION = "Software sob medida, automação de processos e sites para pequenas e médias empresas em todo o Brasil e em Portugal. Diagnóstico inicial sem custo.";

const float = keyframes`
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-9px); }
`;

const Page = styled.main`
    width: 100%;
    overflow-x: hidden;
`;

const Section = styled.section`
    width: min(1180px, calc(100vw - 32px));
    margin: 0 auto;

    @media only screen and (max-width: 640px) {
        width: min(100%, calc(100vw - 24px));
    }
`;

const SectionTop = styled(Section)`
    padding-top: ${({ $pad = "72px" }) => $pad};

    @media only screen and (max-width: 640px) {
        padding-top: ${({ $padMobile = "48px" }) => $padMobile};
    }
`;

const Eyebrow = styled.span`
    font-size: 12.5px;
    font-weight: 800;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: ${({ $tone }) => ($tone === "success" ? "var(--success-strong)" : "var(--brand)")};
`;

const SectionHeading = styled.h2`
    margin: 8px 0 0;
    font-family: var(--font-display);
    font-weight: 800;
    font-size: clamp(1.9rem, 3.2vw, 2.35rem);
    line-height: 1.1;
    letter-spacing: -0.02em;
    color: var(--ink);
    text-wrap: balance;
`;

const SectionText = styled.p`
    margin: 0;
    max-width: ${({ $max = "56ch" }) => $max};
    color: var(--muted);
    font-size: 1.02rem;
    font-weight: 500;
    line-height: 1.65;
`;

/* ---------- Hero ---------- */

const Hero = styled(SectionTop)`
    display: grid;
    grid-template-columns: minmax(0, 1fr) 260px;
    gap: 40px;
    align-items: center;
    padding-top: 168px;

    @media only screen and (max-width: 900px) {
        grid-template-columns: 1fr;
        padding-top: 132px;
    }

    @media only screen and (max-width: 640px) {
        padding-top: 118px;
    }
`;

const HeroCopy = styled.div`
    display: flex;
    flex-direction: column;
    gap: 18px;
`;

const HeroTitle = styled.h1`
    margin: 0;
    font-family: var(--font-display);
    font-weight: 800;
    font-size: clamp(2.4rem, 5vw, 3.6rem);
    line-height: 1.05;
    letter-spacing: -0.03em;
    color: var(--ink);
    max-width: 24ch;
    text-wrap: balance;
`;

const HeroLead = styled.p`
    margin: 0;
    max-width: 56ch;
    font-size: 1.14rem;
    font-weight: 500;
    line-height: 1.6;
    color: var(--muted);
`;

const HeroMeta = styled.div`
    display: flex;
    align-items: center;
    gap: 22px;
    flex-wrap: wrap;
`;

const HeroMetaItem = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 14.5px;
    font-weight: 600;
    color: var(--muted);

    .material-symbols-rounded { color: var(--success); }
`;

const HeroImage = styled.img`
    width: 100%;
    max-width: 220px;
    height: auto;
    margin: 0 auto;
    filter: drop-shadow(0 22px 38px rgba(109, 63, 232, 0.18));
    animation: ${float} 5.4s ease-in-out infinite;

    @media only screen and (max-width: 900px) {
        max-width: 160px;
    }
`;

/* ---------- Diagnostic tabs ---------- */

const DiagHeader = styled.div`
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 20px;
    flex-wrap: wrap;
    padding-bottom: 12px;
`;

const DiagHeaderTitle = styled.h2`
    margin: 0;
    font-size: 17px;
    font-weight: 800;
    color: var(--ink);

    span { font-weight: 600; color: var(--muted); }
`;

const DiagHeaderNote = styled.span`
    font-size: 13.5px;
    font-weight: 600;
    color: var(--muted);
`;

const TabGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 12px;

    @media only screen and (max-width: 900px) {
        grid-template-columns: 1fr 1fr;
    }

    @media only screen and (max-width: 640px) {
        grid-template-columns: 1fr;
    }
`;

const TabButton = styled.button`
    all: unset;
    display: flex;
    flex-direction: column;
    gap: 11px;
    align-items: flex-start;
    text-align: left;
    padding: 18px;
    border-radius: 18px;
    cursor: pointer;
    box-sizing: border-box;
    transition: border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;
    background: #fff;

    ${({ $on }) => ($on
        ? css`border: 2px solid var(--brand); box-shadow: 0 14px 34px rgba(109, 63, 232, 0.16);`
        : css`border: 1.5px solid var(--line); box-shadow: 0 1px 2px rgba(26, 19, 48, 0.05);`)}

    &:hover { border-color: #c9bdea; transform: translateY(-2px); }
    &:focus-visible { outline: 2px solid var(--brand); outline-offset: 3px; }
`;

const TabTopRow = styled.span`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    width: 100%;
`;

const TabIcon = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 12px;
    flex: none;
    background: ${({ $on }) => ($on ? "var(--brand)" : "#f2ecfb")};
    color: ${({ $on }) => ($on ? "#fff" : "var(--brand-2)")};
`;

const TabBadge = styled.span`
    display: inline-flex;
    align-items: center;
    min-height: 22px;
    padding: 0 8px;
    border-radius: 12px;
    font-size: 10.5px;
    font-weight: 800;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    background: var(--success-bg);
    color: var(--success-strong);
`;

const TabTitle = styled.strong`
    font-size: 17px;
    font-weight: 800;
    line-height: 1.25;
    letter-spacing: -0.015em;
    color: var(--ink);
`;

const TabCommonTag = styled.span`
    display: inline-flex;
    align-items: center;
    width: fit-content;
    min-height: 22px;
    padding: 0 8px;
    border-radius: 12px;
    background: #f4f1fb;
    color: var(--brand-2);
    font-size: 10.5px;
    font-weight: 800;
    letter-spacing: 0.06em;
    text-transform: uppercase;
`;

const TabHint = styled.span`
    font-size: 14px;
    font-weight: 500;
    line-height: 1.5;
    color: var(--muted);
`;

const DiagPanel = styled.div`
    margin-top: 18px;
    padding: 32px;
    border-radius: 22px;
    background: var(--bg-soft);
    border: 1px solid var(--line);
    display: grid;
    grid-template-columns: minmax(0, 1.15fr) minmax(0, 0.85fr);
    gap: 40px;

    @media only screen and (max-width: 900px) {
        grid-template-columns: 1fr;
        gap: 24px;
        padding: 22px;
    }
`;

const PanelLeft = styled.div`
    display: flex;
    flex-direction: column;
    gap: 18px;
`;

const SelectedTag = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    width: fit-content;
    min-height: 30px;
    padding: 0 12px;
    border-radius: 12px;
    background: var(--success-bg);
    color: var(--success-strong);
    font-size: 12.5px;
    font-weight: 800;
`;

const PanelTitle = styled.h3`
    margin: 0;
    font-size: clamp(1.5rem, 2.6vw, 2rem);
    font-weight: 800;
    line-height: 1.15;
    letter-spacing: -0.02em;
    color: var(--ink);
    text-wrap: balance;
`;

const PanelBody = styled.p`
    margin: 0;
    max-width: 54ch;
    font-size: 1.02rem;
    font-weight: 500;
    line-height: 1.6;
    color: var(--muted);
`;

const StepsGrid = styled.div`
    display: grid;
    gap: 10px;
`;

const StepRow = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 14px 16px;
    border-radius: 12px;
    background: #fff;
    border: 1px solid var(--line);

    .material-symbols-rounded { color: var(--success); margin-top: 1px; }
`;

const StepText = styled.span`
    font-size: 15px;
    font-weight: 600;
    line-height: 1.5;
    color: var(--ink);
`;

const MetaStrip = styled.div`
    display: flex;
    align-items: center;
    gap: 20px;
    flex-wrap: wrap;
    padding: 16px 18px;
    border-radius: 12px;
    background: #fff;
    border: 1px solid var(--line);
`;

const MetaCol = styled.span`
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

const MetaLabel = styled.span`
    font-size: 11.5px;
    font-weight: 800;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--muted);
`;

const MetaValue = styled.strong`
    font-size: 16px;
    font-weight: 800;
    color: var(--ink);
`;

const MetaDivider = styled.span`
    width: 1px;
    height: 32px;
    background: var(--line);
`;

const PanelActions = styled.div`
    display: flex;
    align-items: center;
    gap: 20px;
    flex-wrap: wrap;
`;

const PanelCta = styled.a`
    display: inline-flex;
    align-items: center;
    gap: 10px;
    min-height: 56px;
    padding: 0 24px;
    border-radius: 12px;
    background: var(--brand);
    color: #fff;
    font-size: 16.5px;
    font-weight: 800;
    box-shadow: 0 10px 26px rgba(109, 63, 232, 0.22);
    transition: background-color 0.2s ease;

    &:hover { background: var(--brand-strong); }
`;

const PanelNote = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 9px;
    font-size: 14.5px;
    font-weight: 600;
    color: var(--success-strong);
`;

const CaseCard = styled.div`
    display: flex;
    flex-direction: column;
    gap: 14px;
    padding: 28px;
    border-radius: 18px;
    background: var(--ink);
    color: #fff;
`;

const CaseClient = styled.span`
    font-size: 12px;
    font-weight: 800;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: var(--brand-3);
`;

const CaseMetric = styled.strong`
    display: block;
    font-size: 42px;
    font-weight: 800;
    line-height: 1;
    letter-spacing: -0.035em;
    color: var(--brand-3);
`;

const CaseMetricLabel = styled.span`
    display: block;
    margin-top: 4px;
    font-size: 15px;
    font-weight: 700;
    color: rgba(255, 255, 255, 0.86);
`;

const CaseQuote = styled.p`
    margin: 0;
    font-family: var(--font-quote);
    font-style: italic;
    font-size: 19px;
    line-height: 1.5;
    color: #fff;
`;

const CaseAuthor = styled.div`
    margin-top: auto;
    padding-top: 16px;
    border-top: 1px solid rgba(255, 255, 255, 0.16);
    display: flex;
    align-items: center;
    gap: 10px;
`;

const CaseInitials = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.14);
    color: #fff;
    font-size: 13px;
    font-weight: 800;
`;

const CaseAuthorName = styled.span`
    font-size: 13.5px;
    font-weight: 700;
    color: rgba(255, 255, 255, 0.82);
`;

const NoCaseCard = styled.div`
    display: flex;
    flex-direction: column;
    gap: 18px;
    padding: 28px;
    border-radius: 18px;
    background: var(--ink);
    color: #fff;
`;

const NoCaseLabel = styled.span`
    font-size: 12px;
    font-weight: 800;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--brand-3);
`;

const NoCaseText = styled.p`
    margin: 0;
    font-size: 17.5px;
    font-weight: 600;
    line-height: 1.55;
    color: #fff;
`;

const ClienteChips = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    padding-top: 16px;
    border-top: 1px solid rgba(255, 255, 255, 0.16);
`;

const ClienteChip = styled.span`
    display: inline-flex;
    align-items: center;
    min-height: 30px;
    padding: 0 12px;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.18);
    font-size: 13px;
    font-weight: 700;
    color: #fff;
`;

const RouteLink = styled.a`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 18px 20px;
    border-radius: 18px;
    background: #fff;
    border: 1px solid var(--line);
    transition: border-color 0.2s ease;

    &:hover { border-color: #c9bdea; }
`;

const RouteLinkTitle = styled.strong`
    display: block;
    font-size: 15px;
    font-weight: 800;
    color: var(--ink);
`;

const RouteLinkPath = styled.span`
    font-size: 13px;
    font-weight: 600;
    color: var(--muted);
`;

/* ---------- Services ---------- */

const ServicesHeader = styled.div`
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 40px;
    flex-wrap: wrap;
    margin-bottom: 24px;
`;

const ServicesGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 12px;

    @media only screen and (max-width: 760px) {
        grid-template-columns: 1fr;
    }
`;

const ServiceCard = styled.a`
    display: grid;
    grid-template-columns: 42px minmax(0, 1fr) 20px;
    gap: 14px;
    align-items: center;
    padding: 18px;
    border-radius: 12px;
    background: #fff;
    border: 1px solid var(--line);
    transition: border-color 0.2s ease;

    &:hover { border-color: #c9bdea; }
`;

const ServiceIcon = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 42px;
    height: 42px;
    border-radius: 12px;
    background: #f2ecfb;
    color: var(--brand-2);
`;

const ServiceTitle = styled.strong`
    display: block;
    font-size: 16px;
    font-weight: 800;
    line-height: 1.25;
    color: var(--ink);
`;

const ServiceShort = styled.span`
    display: block;
    margin-top: 3px;
    font-size: 13.5px;
    font-weight: 500;
    line-height: 1.45;
    color: var(--muted);
`;

/* ---------- Onde atende ---------- */

const WhereGrid = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 0.95fr) minmax(0, 1.05fr);
    gap: 48px;
    align-items: start;

    @media only screen and (max-width: 860px) {
        grid-template-columns: 1fr;
    }
`;

const ChecklistItem = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 11px;

    .material-symbols-rounded { color: var(--success); margin-top: 2px; }
`;

const ChecklistText = styled.p`
    margin: 0;
    font-size: 15.5px;
    font-weight: 600;
    line-height: 1.55;
    color: var(--ink);
`;

const ClientsPanel = styled.div`
    display: flex;
    flex-direction: column;
    gap: 14px;
    padding: 28px;
    border-radius: 18px;
    background: var(--bg-soft);
    border: 1px solid var(--line);
`;

const ClientsLabel = styled.span`
    font-size: 12px;
    font-weight: 800;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--muted);
`;

const ClientsLogos = styled.div`
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 20px;
    align-items: center;
    justify-items: center;

    img {
        height: 52px;
        width: auto;
        max-width: 100%;
        object-fit: contain;
    }
`;

const ClientsCaption = styled.p`
    margin: 0;
    padding-top: 16px;
    border-top: 1px solid var(--line);
    font-size: 15px;
    font-weight: 500;
    line-height: 1.6;
    color: var(--muted);
`;

const ClientsCasesLink = styled.a`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 14.5px;
    font-weight: 800;
    color: var(--brand);

    &:hover { color: var(--brand-2); }
`;

const CoverageNote = styled.p`
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    line-height: 1.6;
    color: var(--success-strong);
    padding: 16px 18px;
    border-radius: 12px;
    background: var(--success-bg);
    border: 1px solid var(--success-line);
`;

/* ---------- Trust stats ---------- */

const TrustGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 16px;

    @media only screen and (max-width: 760px) {
        grid-template-columns: 1fr 1fr;
    }
`;

const TrustCard = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 24px;
    border-radius: 18px;
    background: var(--bg-soft);
    border: 1px solid var(--line);
`;

const TrustValue = styled.strong`
    font-size: 30px;
    font-weight: 800;
    line-height: 1.05;
    letter-spacing: -0.03em;
    color: var(--brand);
`;

const TrustLabel = styled.span`
    font-size: 15px;
    font-weight: 700;
    color: var(--ink);
`;

const TrustNote = styled.span`
    font-size: 13.5px;
    font-weight: 500;
    line-height: 1.5;
    color: var(--muted);
`;

/* ---------- FAQ ---------- */

const FaqGrid = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 0.72fr) minmax(0, 1.28fr);
    gap: 48px;
    align-items: start;

    @media only screen and (max-width: 860px) {
        grid-template-columns: 1fr;
        gap: 24px;
    }
`;

const FaqList = styled.div`
    display: grid;
    gap: 10px;
`;

const FaqItem = styled.details`
    border-radius: 12px;
    background: #fff;
    border: 1px solid var(--line);

    summary {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 10px;
        padding: 18px 20px;
        cursor: pointer;
        list-style: none;
        font-size: 16px;
        font-weight: 800;
        color: var(--ink);
    }

    summary::-webkit-details-marker { display: none; }

    .material-symbols-rounded { color: var(--brand); flex: none; }
`;

const FaqAnswer = styled.p`
    margin: 0;
    padding: 0 20px 18px;
    font-size: 15px;
    font-weight: 500;
    line-height: 1.6;
    color: var(--muted);
`;

/* ---------- Form section ---------- */

const FormSection = styled.div`
    margin-top: 76px;
    background: var(--ink);
`;

const FormGrid = styled(Section)`
    padding: 68px 0;
    display: grid;
    grid-template-columns: minmax(0, 0.88fr) minmax(0, 1.12fr);
    gap: 56px;
    align-items: start;

    @media only screen and (max-width: 900px) {
        grid-template-columns: 1fr;
        gap: 32px;
        padding: 48px 0;
    }
`;

const FormCopy = styled.div`
    display: flex;
    flex-direction: column;
    gap: 22px;
`;

const FormTitle = styled.h2`
    margin: 0;
    font-size: clamp(1.7rem, 3vw, 2.2rem);
    font-weight: 800;
    line-height: 1.1;
    letter-spacing: -0.02em;
    color: #fff;
    max-width: 22ch;
`;

const FormLead = styled.p`
    margin: 0;
    max-width: 46ch;
    font-size: 16.5px;
    font-weight: 500;
    line-height: 1.62;
    color: rgba(255, 255, 255, 0.78);
`;

const ContactOptions = styled.div`
    display: grid;
    gap: 12px;
`;

const ContactOption = styled.a`
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 18px 20px;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.07);
    border: 1px solid rgba(255, 255, 255, 0.16);
    transition: background-color 0.2s ease;

    &:hover { background: rgba(255, 255, 255, 0.12); }

    .material-symbols-rounded { color: var(--brand-3); font-size: 24px; }
`;

const ContactOptionTitle = styled.strong`
    display: block;
    font-size: 16px;
    font-weight: 800;
    color: #fff;
`;

const ContactOptionNote = styled.span`
    display: block;
    margin-top: 2px;
    font-size: 14px;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.72);
    overflow-wrap: anywhere;
`;

function Home() {
    const location = useLocation();
    const [selectedId, setSelectedId] = React.useState("manual");

    React.useEffect(() => {
        const params = new URLSearchParams(location.search);
        const situacao = params.get("situacao");
        if (situacao && DIAGNOSTICO.some((item) => item.id === situacao)) {
            setSelectedId(situacao);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    React.useEffect(() => {
        document.title = HOME_TITLE;
        setMetaTag('meta[name="description"]', { name: "description", content: HOME_DESCRIPTION });
        setMetaTag('meta[property="og:title"]', { property: "og:title", content: HOME_TITLE });
        setMetaTag('meta[property="og:description"]', { property: "og:description", content: HOME_DESCRIPTION });
        setMetaTag('meta[property="og:url"]', { property: "og:url", content: SITE_URL });
        setLinkTag('link[rel="canonical"]', { rel: "canonical", href: SITE_URL });
        setStructuredData("home-org-schema", {
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "Mango Software House",
            url: SITE_URL,
            description: HOME_DESCRIPTION,
            taxID: "33.545.792/0001-39",
            email: "contato@mangosoftwarehouse.com",
            telephone: "+55 41 9115-0056",
            areaServed: [{ "@type": "Country", name: "Brasil" }, { "@type": "Country", name: "Portugal" }]
        });
        setStructuredData("home-faq-schema", {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQ_GERAL.map((item) => ({
                "@type": "Question",
                name: item.q,
                acceptedAnswer: { "@type": "Answer", text: item.a }
            }))
        });

        return () => {
            removeStructuredData("home-org-schema");
            removeStructuredData("home-faq-schema");
            restoreDefaultMeta();
        };
    }, []);

    const selected = DIAGNOSTICO.find((item) => item.id === selectedId) || DIAGNOSTICO[0];
    const tabsData = DIAGNOSTICO.filter((item) => item.id !== "qa");

    function selecionar(id) {
        setSelectedId(id);
    }

    function handleTabKeyDown(event, index) {
        let target = null;
        if (event.key === "ArrowRight" || event.key === "ArrowDown") target = (index + 1) % tabsData.length;
        else if (event.key === "ArrowLeft" || event.key === "ArrowUp") target = (index - 1 + tabsData.length) % tabsData.length;
        else if (event.key === "Home") target = 0;
        else if (event.key === "End") target = tabsData.length - 1;
        if (target === null) return;
        event.preventDefault();
        const nextId = tabsData[target].id;
        setSelectedId(nextId);
        requestAnimationFrame(() => {
            const el = document.getElementById(`tab-${nextId}`);
            if (el) el.focus();
        });
    }

    const formOpcoes = tabsData.map((item) => ({
        id: item.id,
        label: item.label,
        on: item.id === selected.id
    }));

    return (
        <>
            <Nav />

            <Page>
                <Hero id="top">
                    <HeroCopy>
                        <Eyebrow $tone="success">Diagnóstico em 1 minuto</Eyebrow>
                        <HeroTitle>Comece pelo que está travando a sua empresa hoje</HeroTitle>
                        <HeroLead>
                            Escolha uma situação abaixo. A Mango mostra o que costuma resolver esse caso e o resultado que um cliente real teve — e o pedido de diagnóstico já sai preenchido com a sua escolha.
                        </HeroLead>
                        <HeroMeta>
                            <HeroMetaItem><Icon name="schedule" size="19px" />Resposta em até 1 dia útil</HeroMetaItem>
                            <HeroMetaItem><Icon name="payments" size="19px" />Conversa inicial sem custo</HeroMetaItem>
                        </HeroMeta>
                    </HeroCopy>
                    <HeroImage src={HeroCutout} alt="" />
                </Hero>

                <SectionTop id="diagnostico" $pad="30px" $padMobile="24px">
                    <DiagHeader>
                        <DiagHeaderTitle>Escolha uma situação <span>— pode trocar quando quiser</span></DiagHeaderTitle>
                        <DiagHeaderNote>{tabsData.length} situações · o diagnóstico e o formulário se ajustam à sua escolha</DiagHeaderNote>
                    </DiagHeader>

                    <TabGrid role="tablist" aria-label="Escolha a situação da sua empresa">
                        {tabsData.map((item, index) => {
                            const on = item.id === selected.id;
                            return (
                                <TabButton
                                    key={item.id}
                                    id={`tab-${item.id}`}
                                    type="button"
                                    role="tab"
                                    aria-selected={on}
                                    tabIndex={on ? 0 : -1}
                                    $on={on}
                                    onClick={() => selecionar(item.id)}
                                    onKeyDown={(event) => handleTabKeyDown(event, index)}
                                >
                                    <TabTopRow>
                                        <TabIcon $on={on}><Icon name={item.icon} size="22px" /></TabIcon>
                                        {on && <TabBadge>Selecionado</TabBadge>}
                                    </TabTopRow>
                                    <TabTitle>{item.label}</TabTitle>
                                    {item.common && <TabCommonTag>situação mais comum</TabCommonTag>}
                                    <TabHint>{item.hint}</TabHint>
                                </TabButton>
                            );
                        })}
                    </TabGrid>

                    <DiagPanel>
                        <PanelLeft>
                            <SelectedTag><Icon name="check_circle" size="16px" />Selecionado: {selected.tag}</SelectedTag>
                            <PanelTitle>{selected.title}</PanelTitle>
                            <PanelBody>{selected.body}</PanelBody>

                            <StepsGrid>
                                {selected.steps.map((step) => (
                                    <StepRow key={step}>
                                        <Icon name="check_circle" size="20px" />
                                        <StepText>{step}</StepText>
                                    </StepRow>
                                ))}
                            </StepsGrid>

                            <MetaStrip>
                                <MetaCol><MetaLabel>Resposta</MetaLabel><MetaValue>até 1 dia útil</MetaValue></MetaCol>
                                <MetaDivider />
                                <MetaCol><MetaLabel>Primeira conversa</MetaLabel><MetaValue>sem custo</MetaValue></MetaCol>
                                <MetaDivider />
                                <MetaCol><MetaLabel>Escopo</MetaLabel><MetaValue>ajustado ao seu orçamento</MetaValue></MetaCol>
                            </MetaStrip>

                            <PanelActions>
                                <PanelCta href="#form">
                                    {selected.cta}
                                    <Icon name="arrow_downward" size="20px" />
                                </PanelCta>
                                <PanelNote><Icon name="task_alt" size="19px" />Sua escolha já está no formulário — não precisa digitar de novo</PanelNote>
                            </PanelActions>
                        </PanelLeft>

                        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                            {!selected.noCase && (
                                <CaseCard>
                                    <CaseClient>{selected.client}</CaseClient>
                                    <div>
                                        <CaseMetric>{selected.metric}</CaseMetric>
                                        <CaseMetricLabel>{selected.metricLabel}</CaseMetricLabel>
                                    </div>
                                    <CaseQuote>“{selected.quote}”</CaseQuote>
                                    <CaseAuthor>
                                        <CaseInitials>{selected.initials}</CaseInitials>
                                        <CaseAuthorName>{selected.author}</CaseAuthorName>
                                    </CaseAuthor>
                                </CaseCard>
                            )}
                            {selected.noCase && (
                                <NoCaseCard>
                                    <NoCaseLabel>Não precisa acertar a categoria</NoCaseLabel>
                                    <NoCaseText>A maioria dos projetos começa com o cliente descrevendo a rotina em duas frases — a classificação é trabalho nosso, não seu.</NoCaseText>
                                    <div>
                                        <span style={{ fontSize: "13px", fontWeight: 700, color: "rgba(255,255,255,.7)" }}>Já resolvemos casos em setores sem nada em comum:</span>
                                        <ClienteChips>
                                            {CLIENTES.map((cliente) => <ClienteChip key={cliente}>{cliente}</ClienteChip>)}
                                        </ClienteChips>
                                    </div>
                                </NoCaseCard>
                            )}

                            <RouteLink href={selected.route}>
                                <span>
                                    <RouteLinkTitle>Ver página completa desse diagnóstico</RouteLinkTitle>
                                    <RouteLinkPath>Entregas, perguntas frequentes e como pedir</RouteLinkPath>
                                </span>
                                <Icon name="arrow_forward" size="22px" color="var(--brand)" />
                            </RouteLink>
                        </div>
                    </DiagPanel>
                </SectionTop>

                <SectionTop id="servicos" $pad="72px">
                    <ServicesHeader>
                        <div>
                            <Eyebrow>Serviços</Eyebrow>
                            <SectionHeading>As seis frentes que a Mango executa</SectionHeading>
                        </div>
                        <SectionText $max="38ch">As situações acima são o caminho rápido. Se você já sabe o nome do que precisa, entre direto pela frente correspondente.</SectionText>
                    </ServicesHeader>

                    <ServicesGrid>
                        {FRENTES.map((frente) => (
                            <ServiceCard key={frente.title} href={frente.route}>
                                <ServiceIcon><Icon name={frente.icon} size="21px" /></ServiceIcon>
                                <span>
                                    <ServiceTitle>{frente.title}</ServiceTitle>
                                    <ServiceShort>{frente.short}</ServiceShort>
                                </span>
                                <Icon name="chevron_right" size="20px" color="var(--muted-soft)" />
                            </ServiceCard>
                        ))}
                    </ServicesGrid>
                </SectionTop>

                <SectionTop $pad="72px">
                    <WhereGrid>
                        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                            <Eyebrow>Onde a Mango atende</Eyebrow>
                            <SectionHeading>Atendemos empresas em todo o Brasil e em Portugal</SectionHeading>
                            <SectionText $max="52ch">Reuniões com hora marcada, entregas visíveis a cada etapa e um canal direto com quem está construindo — do começo do projeto ao suporte depois da entrega.</SectionText>
                            <div style={{ display: "flex", flexDirection: "column", gap: "10px", paddingTop: "6px" }}>
                                <ChecklistItem><Icon name="check_circle" size="20px" /><ChecklistText>Reuniões em português, no seu fuso, com hora marcada</ChecklistText></ChecklistItem>
                                <ChecklistItem><Icon name="check_circle" size="20px" /><ChecklistText>Contrato e nota fiscal para empresas no Brasil e em Portugal</ChecklistText></ChecklistItem>
                                <ChecklistItem><Icon name="check_circle" size="20px" /><ChecklistText>Suporte no mesmo canal depois da entrega</ChecklistText></ChecklistItem>
                            </div>
                        </div>

                        <ClientsPanel>
                            <ClientsLabel>Empresas que já contrataram</ClientsLabel>
                            <ClientsLogos>
                                {PROJECT_CASES.map((project) => (
                                    <img key={project.name} src={project.logo} alt={project.name} />
                                ))}
                            </ClientsLogos>
                            <ClientsCaption>Seguros, colecionáveis, detalhe automotivo e gastronomia — setores sem nada em comum além de um gargalo real que precisava sair da planilha ou do Instagram.</ClientsCaption>
                            <ClientsCasesLink href="/cases">Ver o que foi entregue em cada um <Icon name="arrow_forward" size="18px" /></ClientsCasesLink>
                        </ClientsPanel>
                    </WhereGrid>
                    <CoverageNote style={{ marginTop: "24px" }}>Se a sua empresa está em qualquer ponto do Brasil ou de Portugal, o processo é exatamente o mesmo.</CoverageNote>
                </SectionTop>

                <SectionTop $pad="56px">
                    <TrustGrid>
                        {TRUST.map((item) => (
                            <TrustCard key={item.label}>
                                <TrustValue>{item.value}</TrustValue>
                                <TrustLabel>{item.label}</TrustLabel>
                                <TrustNote>{item.note}</TrustNote>
                            </TrustCard>
                        ))}
                    </TrustGrid>
                </SectionTop>

                <SectionTop $pad="72px">
                    <FaqGrid>
                        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                            <Eyebrow>Perguntas frequentes</Eyebrow>
                            <SectionHeading>As dúvidas que mais chegam pelo WhatsApp</SectionHeading>
                            <SectionText $max="40ch">Se a sua não estiver aqui, pergunte direto — respondemos no mesmo dia útil.</SectionText>
                        </div>
                        <FaqList>
                            {FAQ_GERAL.map((item) => (
                                <FaqItem key={item.q}>
                                    <summary>{item.q}<Icon name="expand_more" size="22px" /></summary>
                                    <FaqAnswer>{item.a}</FaqAnswer>
                                </FaqItem>
                            ))}
                        </FaqList>
                    </FaqGrid>
                </SectionTop>

                <FormSection id="form">
                    <FormGrid>
                        <FormCopy>
                            <FormTitle>Pedir o diagnóstico do seu caso</FormTitle>
                            <FormLead>A situação que você escolheu lá em cima já está marcada aqui. Faltam três campos.</FormLead>

                            <ContactOptions>
                                <ContactOption href={waLink(`Olá! Vim pelo site. Minha situação: ${selected.label}.`)} target="_blank" rel="noreferrer">
                                    <Icon name="chat" size="24px" />
                                    <span>
                                        <ContactOptionTitle>Prefere WhatsApp?</ContactOptionTitle>
                                        <ContactOptionNote>A mensagem já vai preenchida com o seu caso</ContactOptionNote>
                                    </span>
                                </ContactOption>
                                <ContactOption href="tel:+554191150056">
                                    <Icon name="call" size="24px" />
                                    <span>
                                        <ContactOptionTitle>+55 41 9115-0056</ContactOptionTitle>
                                        <ContactOptionNote>Ligação em horário comercial de Brasília</ContactOptionNote>
                                    </span>
                                </ContactOption>
                                <ContactOption href="mailto:contato@mangosoftwarehouse.com">
                                    <Icon name="mail" size="24px" />
                                    <span>
                                        <ContactOptionTitle>contato@mangosoftwarehouse.com</ContactOptionTitle>
                                        <ContactOptionNote>Para escopos que já vêm por escrito</ContactOptionNote>
                                    </span>
                                </ContactOption>
                            </ContactOptions>
                        </FormCopy>

                        <FormularioDiagnostico
                            situacao={selected.tag}
                            exemplo={selected.placeholder}
                            permitirCurto={selected.id === "outro"}
                            opcoes={formOpcoes}
                            onOpcaoSelect={selecionar}
                        />
                    </FormGrid>
                </FormSection>
            </Page>

            <Footer />
        </>
    );
}

export { Home };
export default Home;
